// src/pages/Cloud/components/fsc/FSCOverviewPanel.js
import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Progress } from 'reactstrap';
import { Card, Spinner, Image } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonGroup } from '@mui/material';

import FSCComplianceDetailPanel from './FSCComplianceDetailPanel';
import { fetchFSCControlTree, resetFSCState } from '../../../../store/slices/fscComplianceSlice';
import { setFscCloudContext, normalizeFscCloud } from '../../../../api/fscCloudContext';
import { useFscProjectBootstrap } from './useFscProjectBootstrap';

import FSCAddClauseSidebar from './FSCAddClauseSidebar';
import FSCEditClauseSidebar from './FSCEditClauseSidebar';
import { CLOUD_SCOPE_ALL, getCloudScopeConfig } from '../../cloudScopeConfig';

/* =========================
 * 計算統計
 * ========================= */
function buildSummaryFromAssessments(assessments = []) {
  let passed = 0;
  let partial = 0;
  let failed = 0;

  for (const a of assessments) {
    if (a.status === 'passed') passed += 1;
    else if (a.status === 'partial') partial += 1;
    else if (a.status === 'failed') failed += 1;
  }

  const total = assessments.length;

  return { passed, partial, failed, total };
}

/* =========================
 * 取得 assessment 的月份（YYYY-MM）
 * ========================= */
function getAssessmentMonth(a) {
  return a?.assessment_month || a?.month || a?.assessmentMonth || '';
}

/* =========================
 * 取得「今天」的月份（Asia/Taipei）
 * ========================= */
function getTodayMonthKey() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' }).slice(0, 7);
}

/** 金管會統計：CIS 取自 summary API，FSC 當月由 assessments 即時計算；部分通過僅 FSC。 */
function buildFscSummaryForCurrentMonth(assessments = [], cisSummary) {
  const todayMonth = getTodayMonthKey();
  const currentMonthAssessments = (assessments || []).filter((a) => getAssessmentMonth(a) === todayMonth);
  const local = buildSummaryFromAssessments(currentMonthAssessments);

  const cisPassed = cisSummary?.passed_count ?? 0;
  const cisFailed = cisSummary?.failed_count ?? 0;
  const cisTotal = cisSummary?.total_count ?? 0;
  const fscEffectivePassed = local.passed + local.partial;

  return {
    passed: cisPassed + fscEffectivePassed,
    partial: local.partial,
    failed: cisFailed + local.failed,
    total: cisTotal + local.total,
  };
}

const STATS_TITLE_STYLE = { fontWeight: 'bold', color: '#5F5F5F' };
const BOLD_TITLE_STYLE = { fontWeight: 'bold' };
const DOT_BASE_STYLE = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  display: 'inline-block',
  marginRight: 6,
};
const HEADER_LAYOUT_STYLE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};

/* =========================
 * 上方統計區塊
 * ========================= */
function FSCStatsRow({ summary, loading }) {
  const { passed, partial, failed, total } = summary;

  const blocks = [
    {
      key: 'passed',
      title: '通過',
      value: passed,
      color: '#138854',
      icon: '/assets/images/ic_trending_down_FILL0_wght400_GRAD0_opsz24.svg',
    },
    {
      key: 'partial',
      title: '部分通過',
      value: partial,
      color: '#CB761B',
      icon: '/assets/images/ic_read_more_FILL0_wght400_GRAD0_opsz24.svg',
    },
    {
      key: 'failed',
      title: '未通過',
      value: failed,
      color: '#C5283D',
      icon: '/assets/images/ic_trending_up_FILL0_wght400_GRAD0_opsz24.svg',
    },
  ];

  const progressValue = total > 0 ? (passed / total) * 100 : 0;

  return (
    <Row className="mt-3 g-3">
      {blocks.map((b) => (
        <Col md={2} key={b.key}>
          <Card style={{ minHeight: 150 }}>
            <Row className="mx-2">
              <Col className="p-4 text-center">
                <h2 style={{ ...BOLD_TITLE_STYLE, color: b.color }}>
                  {loading ? <Spinner animation="border" size="sm" /> : b.value}
                </h2>
              </Col>
            </Row>

            <Row className="mx-1">
              <Col className="text-center">
                <Image alt={b.title} src={b.icon} style={{ marginRight: 6 }} />
                <span style={STATS_TITLE_STYLE}>{b.title}</span>
              </Col>
            </Row>
          </Card>
        </Col>
      ))}

      <Col md={6}>
        <Card style={{ minHeight: '150px' }}>
          <Card.Body className="d-flex flex-column justify-content-between">
            <div style={STATS_TITLE_STYLE}>總合規通過數</div>

            <div className="d-flex align-items-center mt-2">
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  marginRight: '16px',
                  minWidth: '90px',
                }}
              >
                {loading ? <Spinner animation="border" size="sm" /> : `${passed}/${total}`}
              </div>

              <style>{`
                .fsc-pass-progress .progress-bar {
                  background-color: #7FBE25 !important;
                }
              `}</style>

              <div style={{ flexGrow: 1 }}>
                <Progress
                  className="fsc-pass-progress"
                  value={progressValue}
                  style={{ height: '12px' }}
                  color="#7FBE25"
                />
              </div>
            </div>

            <div className="d-flex align-items-center mt-3" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
              <div className="d-flex align-items-center me-4">
                <span
                  style={{
                    ...DOT_BASE_STYLE,
                    backgroundColor: '#7FBE25',
                  }}
                />
                通過
              </div>

              <div className="d-flex align-items-center">
                <span
                  style={{
                    ...DOT_BASE_STYLE,
                    backgroundColor: '#CED4DA',
                  }}
                />
                未通過／部分通過
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

function FSCClauseActionButtons({ onAdd, onEdit }) {
  return (
    <ButtonGroup size="small" color="inherit" variant="outlined" aria-label="clause-actions">
      <Button onClick={onAdd}>新增條款</Button>
      <Button onClick={onEdit} variant="outlined">
        編輯條款
      </Button>
    </ButtonGroup>
  );
}

/* =========================
 * 主元件
 * ========================= */
export default function FSCOverviewPanel({
  projectUuid,
  platform,
  assessmentResources,
  resourcesLoading,
  onLoadResources,
  cisSummary,
}) {
  const dispatch = useDispatch();
  const { assessments, assessmentsLoading, controlTree } = useSelector((state) => state.fscCompliance);
  const summaryLoading = useSelector((state) => state.compliance.summaryLoading);
  const selectedCloudScope = useSelector((state) => state.compliance.selectedCloudScope);

  const isAdmin = useSelector((state) => state.user?.isAdmin);
  const isEdcAdmin = useSelector((state) => state.businessMembers?.isEdcAdmin);

  const fscCloud = useMemo(() => normalizeFscCloud(platform) || 'azure', [platform]);
  const scopeEnabled = getCloudScopeConfig(fscCloud).enabled;
  const cloudScope = selectedCloudScope || CLOUD_SCOPE_ALL;
  const canEditFsc = Boolean(
    (isAdmin || isEdcAdmin) && (!scopeEnabled || (cloudScope && cloudScope !== CLOUD_SCOPE_ALL))
  );

  useEffect(() => {
    setFscCloudContext(fscCloud);
    dispatch(resetFSCState());
  }, [dispatch, fscCloud, cloudScope]);

  useFscProjectBootstrap(projectUuid, fscCloud, cloudScope);

  const [showAddClauseSidebar, setShowAddClauseSidebar] = useState(false);
  const [showEditClauseSidebar, setShowEditClauseSidebar] = useState(false);

  useEffect(() => {
    // 不讓已開啟的新增／編輯流程跨到另一個帳戶或專案範圍。
    setShowAddClauseSidebar(false);
    setShowEditClauseSidebar(false);
  }, [fscCloud, cloudScope]);

  const monthKey = useMemo(() => getTodayMonthKey(), []);

  const cloudAssessments = useMemo(() => {
    return (assessments || []).filter((a) => {
      const c = normalizeFscCloud(a?.cloud_provider);
      return !c || c === fscCloud;
    });
  }, [assessments, fscCloud]);

  // 僅統計「今天月份」
  const summary = useMemo(
    () => buildFscSummaryForCurrentMonth(cloudAssessments, cisSummary),
    [cloudAssessments, cisSummary]
  );

  const statsLoading = assessmentsLoading || summaryLoading;

  const ensureTreeLoaded = () => {
    if (!projectUuid) return;

    // 避免點擊時整頁重新 loading：只有在 tree 還沒載到時才補抓
    const treeArr = Array.isArray(controlTree) ? controlTree : [];
    if (treeArr.length === 0) {
      dispatch(
        fetchFSCControlTree({
          project_uuid: projectUuid,
          cloud: fscCloud,
          platform: fscCloud,
          cloud_scope: cloudScope,
        })
      );
    }
  };

  const handleOpenAddClause = () => {
    if (!projectUuid) return;
    ensureTreeLoaded();
    setShowAddClauseSidebar(true);
  };

  const handleOpenEditClause = () => {
    if (!projectUuid) return;
    ensureTreeLoaded();

    setShowEditClauseSidebar(true);
  };

  if (!projectUuid) {
    return (
      <Card>
        <Card.Body>找不到 project_uuid，請先選擇專案。</Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Row>
        <Col>
          <h5 style={BOLD_TITLE_STYLE}>金管會合規評估</h5>
        </Col>
      </Row>

      <FSCStatsRow summary={summary} loading={statsLoading} />

      <Row className="mt-4">
        <Col>
          <Card>
            <Card.Header style={HEADER_LAYOUT_STYLE}>
              <h5 style={{ ...BOLD_TITLE_STYLE, margin: 0 }}>控制項詳細列表</h5>
              {canEditFsc ? <FSCClauseActionButtons onAdd={handleOpenAddClause} onEdit={handleOpenEditClause} /> : null}
            </Card.Header>

            <Card.Body className="p-0">
              <FSCComplianceDetailPanel
                projectUuid={projectUuid}
                compliancePlatform={fscCloud}
                assessmentResources={assessmentResources}
                resourcesLoading={resourcesLoading}
                onLoadResources={onLoadResources}
                canEditFsc={canEditFsc}
                cloudScope={cloudScope}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <FSCEditClauseSidebar
        key={`fsc-edit:${projectUuid}:${fscCloud}:${cloudScope}`}
        show={showEditClauseSidebar}
        onHide={() => setShowEditClauseSidebar(false)}
        projectUuid={projectUuid}
        cloud={fscCloud}
        cloudScope={cloudScope}
        tree={Array.isArray(controlTree) ? controlTree : []}
        monthKey={monthKey}
      />

      <FSCAddClauseSidebar
        key={`fsc-add:${projectUuid}:${fscCloud}:${cloudScope}`}
        show={showAddClauseSidebar}
        onHide={() => setShowAddClauseSidebar(false)}
        projectUuid={projectUuid}
        cloud={fscCloud}
        cloudScope={cloudScope}
        tree={Array.isArray(controlTree) ? controlTree : []}
      />
    </>
  );
}
